import React, { useState } from 'react'
import { css } from '@emotion/core'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/atoms'
import { PaxFullObject } from '@/models/guest-room/guest-room-assign'
import { MiwaLockStatus } from '@/constants/miwa-lock'
import { PaxModal } from '@/components/organisms/customer/pax-modal'
import { DatePicker } from '@/components/organisms/customer/_guest-room/date-picker'
import { RoomTypeSelect } from '@/components/organisms/customer/_guest-room/room-type-select'
import { RoomNumberSelect } from '@/components/organisms/customer/_guest-room/room-number-select'
import { AdminGuestRoomResponseType } from '@/models/admin-guest-room'
import { GuestRoomSelectableResponseType } from '@/models/room-manager/response/guest-room-selectable'
import { RoomAssignStatusSelect } from '@/components/molecules/room-manager/room-assign-status-select'
import { EditRoomType } from '@/hooks/use-edit-guest-rooms'

type Props = {
  room: EditRoomType
  roomIndex: number
  hasSmartKeyPlugin: boolean
  canIssueMiwaCardKey: boolean
  miwaButtonType: (issueType: string) => number
  onOpenKeyCardWriteModal: (room: any) => void
  getGuestRoomKeyNumber: (room: any) => string
  onEditRoomAssignGuestName: (event: any, index: number) => void
  onEditRoomAssignPax: (event: any, index: number, type?: string) => void
  onEditPaxFull: (value: PaxFullObject, index: number) => void
  isCurrentDateSameOrBeforeCheckoutDate: boolean
  isEdit: boolean
  setIsEdit: React.Dispatch<React.SetStateAction<boolean>>
  editGuestRooms: EditRoomType[]
  setEditGuestRooms: React.Dispatch<React.SetStateAction<EditRoomType[]>>
  deletedEditGuestRooms: Array<{ guestRoomAssignId: string }>
  setDeletedEditGuestRooms: React.Dispatch<React.SetStateAction<Array<{ guestRoomAssignId: string }>>>
  hotelGuestRooms: AdminGuestRoomResponseType['guestRoomInfo']
  roomsSelectable: GuestRoomSelectableResponseType['guestRooms']
  onSavedData: () => Promise<any>
}

export const FreeEditFormItem: React.FC<Props> = ({
  room,
  roomIndex,
  miwaButtonType,
  hasSmartKeyPlugin,
  canIssueMiwaCardKey,
  onOpenKeyCardWriteModal,
  getGuestRoomKeyNumber,
  onEditRoomAssignGuestName,
  onEditRoomAssignPax,
  onEditPaxFull,
  isCurrentDateSameOrBeforeCheckoutDate,
  isEdit,
  setIsEdit,
  editGuestRooms,
  setEditGuestRooms,
  deletedEditGuestRooms,
  setDeletedEditGuestRooms,
  hotelGuestRooms,
  roomsSelectable,
  onSavedData,
}) => {
  const { t, i18n } = useTranslation()
  const [isShowPaxModal, setIsShowPaxModal] = useState(false)

  /**
   * Delete button for each room
   */
  const onDeleteRoom = (selectIndex: number) => {
    const _editGuestRooms = [...editGuestRooms]
    const selectGuestRoomAssignId = _editGuestRooms[selectIndex]?.guestRoomAssignId
    if (selectGuestRoomAssignId) {
      setDeletedEditGuestRooms([...deletedEditGuestRooms, { guestRoomAssignId: selectGuestRoomAssignId }])
    }
    _editGuestRooms.splice(selectIndex, 1)

    setEditGuestRooms(_editGuestRooms)
  }

  /**
   * Processing when changing the room type of each room
   */
  const onEditRoomType = (event: React.ChangeEvent<HTMLSelectElement>, selectIndex: number) => {
    // If no room type is selected, stop processing
    if (!event.target.value || event.target.value === '選択してください') {
      alert(t('Please select a room type'))
      event.preventDefault()
      return
    }

    // Create a new array to update the state
    const _editGuestRooms = [...editGuestRooms]

    hotelGuestRooms.forEach(room => {
      if (room.typeNameJa === event.target.value) {
        const { roomTypeId, typeNameJa, typeNameEn } = room
        _editGuestRooms[selectIndex] = {
          ..._editGuestRooms[selectIndex],
          roomTypeId,
          typeNameJa,
          typeNameEn,
          roomNumber: null,
          guestRoomId: null,
        }
      }
    })

    setEditGuestRooms(_editGuestRooms)
    setIsEdit(true)
  }

  /**
   * Processing when changing the room number of each room
   */
  const onEditRoomNumber = (event: React.ChangeEvent<HTMLSelectElement>, selectGuestRoomId: string, selectIndex: number) => {
    // If no room number is selected, stop processing
    if (!selectGuestRoomId) {
      alert(t('Please select a room number'))
      event.preventDefault()
      return
    }
    // Create a new array to update the state
    const _editGuestRooms = [...editGuestRooms]
    _editGuestRooms[selectIndex].guestRoomId = selectGuestRoomId
    _editGuestRooms[selectIndex].roomNumber = getRoomNumberByGuestRoomId(selectGuestRoomId)

    setEditGuestRooms(_editGuestRooms)
    setIsEdit(true)
  }

  /**
   * Get the room number based on guestRoomId
   */
  const getRoomNumberByGuestRoomId = (guestRoomId: string) => {
    let roomNumber = ''
    hotelGuestRooms.forEach(hotelGuestRoom => {
      if (hotelGuestRoom.roomNumber) {
        Object.keys(hotelGuestRoom.roomNumber).forEach(key => {
          hotelGuestRoom.roomNumber[key].forEach(room => {
            if (room.guestRoomId === guestRoomId) {
              roomNumber = room.roomNumber
            }
          })
        })
      }
    })
    return roomNumber
  }

  /**
   * Get a list of currently selectable room types
   */
  const guestRoomTypeList = (roomInfo: EditRoomType) => {
    const typeName = i18n.language === 'ja' ? 'typeNameJa' : 'typeNameEn'
    const list = [
      {
        id: 1,
        name: '選択してください',
        value: '',
      },
    ]
    // Delete the selected room from the list once to avoid duplication of rooms.
    const rooms = roomsSelectable.filter(room => Number(roomInfo.roomTypeId) !== Number(room.roomTypeId))
    rooms.forEach(data => {
      list.push({
        id: Number(data.roomTypeId) + 1,
        name: data[typeName],
        value: data[typeName],
      })
    })
    // Add the selected room type to the hotel-wide list of rooms if the selected room type is not in the selectable array
    if (
      roomInfo &&
      roomInfo.roomTypeId &&
      hotelGuestRooms &&
      hotelGuestRooms.findIndex(room => room.roomTypeId === roomInfo.roomTypeId) !== -1
    ) {
      list.push({
        id: Number(roomInfo.roomTypeId) + 1,
        name: roomInfo[typeName] ?? '',
        value: roomInfo[typeName] ?? '',
      })
    }
    // Sort ascending by roomTypeId (id = 1 is 'Please select')
    list.sort((a, b) => {
      return a.id - b.id
    })
    return list
  }

  /**
   * Get a list of selectable room numbers from the selected room type
   */
  const guestRoomNumberList = (roomInfo: EditRoomType) => {
    const list = [
      {
        name: '選択してください',
        value: '',
        guestRoomId: '',
      },
    ]

    // If room type is selected
    if (roomInfo && roomInfo.typeNameJa) {
      roomsSelectable.forEach(data => {
        if (data.rooms && data.rooms.length && data.typeNameJa === roomInfo.typeNameJa) {
          data.rooms.forEach(room => {
            list.push({
              name: room.roomNumber,
              value: room.guestRoomId,
              guestRoomId: room.guestRoomId,
            })
          })
        }
      })
      if (roomInfo.roomNumber && roomInfo.guestRoomId) {
        let isDuplicate = false
        list.filter(v => {
          if (v.value === roomInfo.guestRoomId) isDuplicate = true
        })
        // Add if not duplicate
        if (!isDuplicate) {
          list.push({
            name: roomInfo.roomNumber,
            value: roomInfo.guestRoomId,
            guestRoomId: roomInfo.guestRoomId,
          })
        }
      }
      // Sort Ascending
      list.sort((a, b) => Number(a.name) - Number(b.name))
    } else {
      // Show all available room numbers if no room type is selected
      const allNumber: { guestRoomId: string; number: string }[] = []
      roomsSelectable.forEach(data => {
        if (data.rooms && data.rooms.length) {
          data.rooms.forEach(room => {
            allNumber.push({
              guestRoomId: room['guestRoomId'],
              number: room['roomNumber'],
            })
          })
        }
      })

      allNumber?.forEach(guestRoom => {
        list.push({
          name: guestRoom['number'],
          value: guestRoom['number'],
          guestRoomId: guestRoom['guestRoomId'],
        })
      })
    }
    return list
  }

  return (
    <tr key={roomIndex}>
      <td>{room?.guestRoomAssignShortId}</td>
      <td>
        <div css={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <DatePicker room={room} dateType="checkin" setIsEdit={setIsEdit} /> ~
          <DatePicker room={room} dateType="checkout" setIsEdit={setIsEdit} />
        </div>
      </td>
      <td>
        <input
          css={inputGuestNameStyle}
          value={room.assignGuestName ? room.assignGuestName : room.reservationGuestName ? room.reservationGuestName : ''}
          onChange={event => onEditRoomAssignGuestName(event, roomIndex)}
          onClick={() => setIsEdit(true)}
        />
      </td>
      <td>
        <div
          onClick={() => {
            setIsEdit(true)
            setIsShowPaxModal(!isShowPaxModal)
          }}
        >
          <input
            css={[inputCapacityStyle, { pointerEvents: 'none' }]}
            value={room.pax ? Number(room.pax).toString() : '0'}
            onChange={event => onEditRoomAssignPax(event, roomIndex)}
            type="number"
            min={0}
          />
        </div>
        <PaxModal
          isOpen={isShowPaxModal}
          onClose={() => setIsShowPaxModal(false)}
          room={room}
          roomIndex={roomIndex}
          onEditPaxFull={onEditPaxFull}
        />
      </td>
      <td>
        <RoomTypeSelect
          room={room}
          roomIndex={roomIndex}
          guestRoomTypeList={guestRoomTypeList}
          onEditRoomType={onEditRoomType}
          isEdit={isEdit}
          cssStyles={{ width: '100%', marginRight: 0, maxWidth: 'initial' }}
        />
      </td>
      <td>
        <RoomNumberSelect
          room={room}
          roomIndex={roomIndex}
          guestRoomNumberList={guestRoomNumberList}
          onEditRoomNumber={onEditRoomNumber}
          isEdit={isEdit}
          cssStyles={{ width: '100%', marginRight: 0, maxWidth: 'initial' }}
        />
      </td>
      <td>
        <RoomAssignStatusSelect room={room as any} onChange={onSavedData} />
      </td>
      <td>
        <div css={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          {canIssueMiwaCardKey && room?.issueType ? (
            !isEdit ? (
              <Button
                width={72}
                height={32}
                fontSize={12}
                buttonType={miwaButtonType(room?.issueType)}
                isDisabled={isEdit || !isCurrentDateSameOrBeforeCheckoutDate}
                onClick={() => onOpenKeyCardWriteModal(room)}
              >
                {room?.issueType === MiwaLockStatus.ISSUE ? t('Issue') : t('Reissue')}
              </Button>
            ) : (
              '-'
            )
          ) : hasSmartKeyPlugin ? (
            getGuestRoomKeyNumber(room)
          ) : (
            '-'
          )}
          {isEdit && (
            <div onClick={() => onDeleteRoom(roomIndex)} css={{ cursor: 'pointer' }}>
              <img src={require('@/static/images/delete_yellow.svg')} width="30" height="30"></img>
            </div>
          )}
        </div>
      </td>
    </tr>
  )
}

const inputGuestNameStyle = css({
  color: '#272727',
  padding: '0 10px',
  height: 32,
  borderRadius: 17,
  marginRight: 16,
  appearance: 'none',
  border: '1px solid #CCC',
  width: '100%',
})

const inputCapacityStyle = css(inputGuestNameStyle, {
  width: '100%',
})
